import { closePanel } from "@/layouts/layouts";
import { computed, ref, Ref } from "vue";
import { toastError } from "@/helpers/toastify";
import {
  confirmDirtyChildrenBeforeSave,
  registerUnsaved,
  runUnsavedSave,
  unregisterUnsaved,
} from "@/core/unsaved-registry";

const PROPS_ERROR = "Не заполнены необходимые поля";

export interface ControllerProps {
  store: Record<string, any>;
  config: Record<string, any>;
  data: Record<string, any> | Ref<Record<string, any>>;
  params: Record<string, any>;
  panel?: Record<string, any>;
}

// Класс-контроллер для управления формой редактирования (кнопки, сохранение, валидация, доступы)
export default class EditController {
  // readonly — вычисляется из props.store.state.readonly
  public readonly readonly: Ref<boolean>;
  // Ссылки на store и data (реактивные)
  protected _store?: Ref<any>;
  protected _data?: Ref<any>;

  // Массив кнопок для панели
  public readonly panelFun = ref<any[]>([]);
  // Стандартные команды для панели (сохранить, закрыть и т.д.)
  public readonly standardCommands: Record<string, any>;

  constructor(protected form: Ref<any>, protected props: ControllerProps) {
    this.readonly = computed(() => props.store.state.readonly);

    this.standardCommands = {
      saveClose: {
        caption: "Сохранить и закрыть",
        title: "Сохранить и закрыть",
        icon: <i class="icon icon-check-circle"></i>,
        class: "btn btn-save",
        onClick: async () => {
          const result = await this.save();
          if (result) {
            this.props.panel?.close?.();
          }
        },
      },
      save: {
        caption: "Сохранить",
        title: "Сохранить",
        icon: <i class="icon icon-save"></i>,
        class: "btn btn-save",
        onClick: async () => await this.save(),
      },
      close: {
        caption: "Закрыть",
        title: "Закрыть",
        class: "btn btn-close",
        onClick: () => {
          if (
            this.props.panel &&
            (this.props.panel as any)._processingReceipt === true
          ) {
            return;
          }
          closePanel(this.props.panel);
        },
      },
    };

    const panelFun: any[] = [];

    if (
      !this.readonly.value &&
      (props.store.model.access.update || props.store.model.access.create)
    ) {
      panelFun.push({ saveClose: {}, save: {} });
    }

    panelFun.push({ close: {} });

    this.createPanelFun(panelFun);

    // Защита метода save от переопределения
    Object.defineProperty(this, "save", {
      value: this.save.bind(this),
      writable: false,
      configurable: false,
    });

    this.registerUnsavedGuard();
  }

  /** Регистрация в сквозном dirty-реестре */
  protected registerUnsavedGuard() {
    const panel = this.props.panel;
    if (!panel?.key) return;
    if (
      panel.skipUnsavedGuard === true ||
      this.props.config?.skipUnsavedGuard === true
    ) {
      return;
    }

    registerUnsaved(panel.key, {
      getChanged: () => {
        if (this.readonly.value) return false;
        if (panel.skipUnsavedGuard === true) return false;
        const store = this.form?.value?.store ?? this.props.store;
        return !!store?.changed;
      },
      save: async () => this.save(false),
      title:
        panel.caption ||
        this.props.store?.model?.description ||
        "Форма",
      parentKey: panel.parentKey ?? null,
      discard: () => {
        const store = this.form?.value?.store ?? this.props.store;
        store?.createHash?.();
      },
    });

    const prevOnClose = panel.onClose?.bind(panel);
    panel.onClose = (result: any) => {
      unregisterUnsaved(panel.key);
      return prevOnClose ? prevOnClose(result) : result;
    };
  }

  // Геттер для store
  get store(): Ref<any> {
    if (!this._store) {
      this._store = computed(() => this.form?.value?.store ?? null);
    }
    return this._store;
  }

  // Геттер для data
  get data(): Ref<any> {
    if (!this._data) {
      this._data = computed(() => this.form?.value?.data ?? {});
    }
    return this._data;
  }

  // Создать массив кнопок для панели
  public createPanelFun(items: any[]) {
    this.panelFun.value.splice(0, this.panelFun.value.length);

    for (const item of items) {
      const panel: any = {};

      for (const key in item) {
        panel[key] = {};

        if (key in this.standardCommands) {
          Object.assign(panel[key], this.standardCommands[key], item[key]);
        } else {
          Object.assign(panel[key], item[key]);
        }
      }

      this.panelFun.value.push(panel);
    }
  }

  // Установить кнопки для панели (альтернативный способ)
  public setButtons() {
    const buttons: any[] = [];

    if (!this.readonly.value) {
      buttons.push({
        caption: "Сохранить",
        class: "btn-action",
        onClick: async () => await this.save(),
      });
    }

    this.props.panel?.setButtons?.(buttons);
  }

  // Метод сохранения (save) — защищён от переопределения
  protected async save(noValidation = false): Promise<boolean> {
    const panelKey = this.props.panel?.key;
    return runUnsavedSave(panelKey, async () => {
      try {
        if (!this.store?.value) {
          console.warn("Store is not initialized yet", {
            form: this.form?.value,
            store: this.store,
          });
          return false;
        }

        if (panelKey) {
          const childrenOk = await confirmDirtyChildrenBeforeSave(panelKey);
          if (!childrenOk) return false;
        }

        // Вызов beforeSave!
        if (!(await this.beforeSave())) {
          return false;
        }

        // Сначала вызываем базовую валидацию
        if (!noValidation) {
          await this.validationBase();
        }

        const saveResult = await this.form.value?.save?.();
        if (!saveResult) return false;

        if (typeof (this as any).afterSave === "function") {
          await (this as any).afterSave();
        }

        // Subtable/offline: данные уже в памяти родителя (markDirty).
        // Сохранение вышестоящей записи — только явно (кнопка / диалог при закрытии).
        // Не эмитим save-data-child: автосейв тяжёлой карты убивает пул БД.

        return true;
      } catch (error) {
        this.handleError(error);
        return false;
      }
    });
  }

  // Метод, который вызывается перед сохранением (можно переопределять)
  protected async beforeSave(): Promise<boolean> {
    return true;
  }

  // Публичный метод для вызова приватного save
  public async triggerSave(noValidation = false): Promise<boolean> {
    return this.save(noValidation);
  }

  // Базовая валидация для UI
  protected async validationBase(): Promise<void> {
    // Сначала вызываем кастомную валидацию
    await this.validate();
    // Потом вызываем валидацию формы для UI
    await this.form.value?.validation?.();
  }

  // Кастомная валидация (можно переопределять)
  protected async validate(): Promise<boolean> {
    return true;
  }

  // Обработка ошибок при сохранении
  protected handleError(error: any): void {
    console.error("Ошибка при сохранении:", error);
    toastError("Ошибка при сохранении данных.");
  }

  // Получить настройки формы
  public settings() {
    return Object.assign({}, this.props.config);
  }

  // Получить набор данных для setup() компонента
  public setup() {
    return {
      readonly: this.readonly,
      controller: this,
      form: this.form,
      settings: this.settings(),
    };
  }
}
